<template>
  <div class="webchat-page">

    <!-- ════════════════════════════════════════════
         DAY LIST PANEL
    ════════════════════════════════════════════ -->
    <div class="list-panel">
      <div class="list-header">
        <h5 class="mb-0 font-weight-bold">Website Chat History</h5>
        <b-button variant="flat-secondary" size="sm" class="btn-icon" :disabled="isLoading" @click="refresh" v-b-tooltip.hover title="Refresh">
          <feather-icon icon="RefreshCwIcon" size="15" :class="isLoading ? 'spin' : ''" />
        </b-button>
      </div>

      <div class="period-toggle">
        <b-button-group size="sm">
          <b-button
            :variant="viewMode === 'day' ? 'primary' : 'outline-secondary'"
            :disabled="isLoading"
            @click="switchViewMode('day')"
          >Day</b-button>
          <b-button
            :variant="viewMode === 'all' ? 'primary' : 'outline-secondary'"
            :disabled="isLoading"
            @click="switchViewMode('all')"
          >Show all chat</b-button>
        </b-button-group>
      </div>

      <!-- Day-grouped view -->
      <div v-if="viewMode === 'day'" class="day-list">
        <div v-if="loadingDays" class="text-center py-3">
          <b-spinner small variant="primary" />
        </div>

        <div v-else-if="days.length === 0" class="day-list-empty">
          <feather-icon icon="CalendarIcon" size="28" class="mb-50 text-muted" />
          <div>No data yet.</div>
        </div>

        <template v-else>
          <div v-for="day in days" :key="day.date" class="day-group">
            <div
              class="day-header"
              :class="{ 'day-header--empty': day.count === 0 }"
              @click="toggleDay(day)"
            >
              <feather-icon :icon="day.expanded ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="15" class="day-header__chevron" />
              <span class="day-header__label">{{ dayLabel(day.date) }}</span>
              <span class="day-header__count" :class="{ 'day-header__count--active': day.count > 0 }">
                {{ day.count }} chat{{ day.count === 1 ? '' : 's' }}
              </span>
            </div>

            <div v-if="day.expanded" class="day-conversations">
              <div v-if="day.loading" class="text-center py-2">
                <b-spinner small variant="primary" />
              </div>
              <div v-else-if="day.conversations.length === 0" class="text-muted small text-center py-2">
                No chats that day
              </div>
              <div
                v-for="conv in day.conversations" :key="conv.user_id"
                class="contact-item"
                :class="{ 'contact-item--active': selectedUser && selectedUser.user_id === conv.user_id }"
                @click="openConversation(conv)"
              >
                <b-avatar :src="conv.picture_url || null" :text="avatarInitial(contactDisplayName(conv))" size="36" variant="light-primary" />
                <div class="contact-body">
                  <span class="contact-name">{{ contactDisplayName(conv) }}</span>
                  <span class="contact-time">{{ formatTime(conv.created_at) }}</span>
                </div>
              </div>
              <div v-if="day.hasMore" class="load-more-row">
                <button class="load-more-btn" :disabled="day.loadingMore" @click="loadMoreDay(day)">
                  <b-spinner v-if="day.loadingMore" small />
                  <span v-else>Load more</span>
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Flat "show all" view -->
      <div v-else class="day-list">
        <div v-if="loadingAll" class="text-center py-3">
          <b-spinner small variant="primary" />
        </div>

        <div v-else-if="allConversations.length === 0" class="day-list-empty">
          <feather-icon icon="UsersIcon" size="28" class="mb-50 text-muted" />
          <div>No website chats yet.</div>
        </div>

        <template v-else>
          <div
            v-for="conv in allConversations" :key="conv.user_id"
            class="contact-item contact-item--flat"
            :class="{ 'contact-item--active': selectedUser && selectedUser.user_id === conv.user_id }"
            @click="openConversation(conv)"
          >
            <b-avatar :src="conv.picture_url || null" :text="avatarInitial(contactDisplayName(conv))" size="36" variant="light-primary" />
            <div class="contact-body">
              <span class="contact-name">{{ contactDisplayName(conv) }}</span>
              <span class="contact-time">{{ formatDateTime(conv.updated_at) }}</span>
            </div>
          </div>
          <div v-if="allHasMore" class="load-more-row">
            <button class="load-more-btn" :disabled="loadingMoreAll" @click="loadMoreAllConversations">
              <b-spinner v-if="loadingMoreAll" small />
              <span v-else>Load more</span>
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- ════════════════════════════════════════════
         READ-ONLY TRANSCRIPT PANEL
    ════════════════════════════════════════════ -->
    <div class="history-panel" :class="{ 'is-open': showHistoryPanel }">
      <div class="sheet-handle d-block d-md-none" @click="showHistoryPanel = false" />

      <div v-if="!selectedUser" class="history-no-selection">
        <feather-icon icon="MessageCircleIcon" size="40" class="text-primary mb-1" />
        <p class="font-weight-bold mb-25">Chat History</p>
        <small class="text-muted">Pick a conversation from the list to view its transcript</small>
      </div>

      <template v-else>
        <div class="history-header">
          <div class="d-flex align-items-center" style="gap:10px;min-width:0">
            <b-avatar :src="selectedUser.picture_url || null" :text="avatarInitial(contactDisplayName(selectedUser))" size="36" variant="light-primary" class="flex-shrink-0" />
            <div style="min-width:0">
              <div class="font-weight-bold" style="word-break:break-all">{{ fullDisplayName(selectedUser) }}</div>
              <small class="text-muted">{{ formatDateTime(selectedUser.created_at) }}</small>
            </div>
          </div>
          <b-button variant="flat-secondary" size="sm" class="btn-icon" @click="closeHistory">
            <feather-icon icon="XIcon" size="18" />
          </b-button>
        </div>

        <!-- Customer profile (built by the bot as the visitor chats) -->
        <div v-if="profileRows.length" class="cust-profile">
          <button class="cust-profile__toggle" @click="profileOpen = !profileOpen">
            <feather-icon icon="ClipboardIcon" size="13" class="mr-50" />
            <span class="flex-grow-1 text-left">Customer profile</span>
            <feather-icon :icon="profileOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="15" />
          </button>
          <div v-if="profileOpen" class="cust-profile__body">
            <div v-for="row in profileRows" :key="row.label" class="cust-profile__row">
              <span class="cust-profile__label">{{ row.label }}</span>
              <span class="cust-profile__value">{{ row.value }}</span>
            </div>
            <div class="cust-profile__foot">
              <span class="text-muted">Updated {{ formatDateTime(customerProfile.updated_at) }}</span>
              <button class="cust-profile__reset" @click="resetProfile">Reset</button>
            </div>
          </div>
        </div>

        <div class="history-body">
          <div v-if="historyLoading" class="text-center py-3">
            <b-spinner small variant="primary" />
            <div class="text-muted small mt-50">Loading messages…</div>
          </div>

          <div v-else-if="chatMessages.length === 0" class="empty-history">
            <feather-icon icon="MessageSquareIcon" size="32" class="text-muted mb-50" />
            <div class="text-muted small">No messages</div>
          </div>

          <div v-else class="msg-list">
            <div
              v-for="(msg, i) in chatMessages" :key="i"
              class="msg-row"
              :class="msg.role === 'user' ? 'msg-row--user' : 'msg-row--bot'"
            >
              <div class="msg-avatar">
                <b-avatar
                  v-if="msg.role === 'user'"
                  :src="selectedUser.picture_url || null"
                  :text="avatarInitial(contactDisplayName(selectedUser))"
                  size="28" variant="light-secondary"
                />
                <b-avatar
                  v-else
                  size="28"
                  :src="msg.agentAvatar || (!msg.agentInitial ? BOT_AVATAR : null)"
                  :variant="msg.agentInitial ? 'light-success' : 'light-primary'"
                  :text="(msg.agentAvatar || !msg.agentInitial) ? null : msg.agentInitial"
                  v-b-tooltip.hover
                  :title="msg.agentName || 'Bot'"
                />
              </div>
              <div class="msg-content" :class="msg.role === 'user' ? 'msg-content--user' : 'msg-content--bot'">
                <div v-if="msg.agentName" class="msg-agent-name">{{ msg.agentName }}</div>
                <div class="msg-bubble" :class="msg.role === 'user' ? 'bubble-user' : 'bubble-bot'">
                  <ChatImage v-if="msg.type === 'image'" :message-id="msg.imageMessageId" />
                  <div v-else class="msg-text">{{ msg.text.trim() }}</div>
                </div>
                <div v-if="msg.time" class="msg-time" :class="msg.role === 'user' ? 'msg-time--left' : 'msg-time--right'">
                  {{ msg.time }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div v-if="showHistoryPanel" class="d-block d-md-none history-backdrop" @click="showHistoryPanel = false" />
  </div>
</template>

<script>
import moment from 'moment'
import chatbotAxios from '@/libs/chatbot-axios'
import {
  BButton, BButtonGroup, BAvatar, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ChatImage from './ChatImage.vue'

// How many days back the "Day" view groups conversations for. There's no UI
// control for this — it's a fixed window; "Show all chat" is the escape
// hatch for anything older.
const DAY_VIEW_WINDOW = 30

// Conversations fetched per "Load more" click, both within an expanded day
// and in the flat "Show all chat" list.
const PAGE_SIZE = 20

export default {
  components: {
    BButton, BButtonGroup, BAvatar, BSpinner, ChatImage,
  },
  directives: { 'b-tooltip': VBTooltip },

  data() {
    return {
      viewMode: 'day', // 'day' | 'all'

      loadingDays: false,
      days: [],

      loadingAll: false,
      loadingMoreAll: false,
      allHasMore: false,
      allConversations: [],

      selectedUser: null,
      showHistoryPanel: false,
      historyLoading: false,
      chatMessages: [],

      // Bot-built customer profile for the open conversation
      customerProfile: null,
      profileOpen: true,

      BOT_AVATAR: 'https://digitalnation.sgp1.cdn.digitaloceanspaces.com/findbangkokroom/storage/1773628173458-default.png',
    }
  },

  computed: {
    isLoading() {
      return this.loadingDays || this.loadingAll
    },
    // The bot-built customer profile as label/value rows, skipping empty fields.
    profileRows() {
      const p = this.customerProfile
      if (!p) return []
      const rows = []
      const money = n => (n == null ? '?' : Number(n).toLocaleString('en-US'))
      if (p.intent && p.intent !== 'unknown') {
        rows.push({ label: 'Looking to', value: { rent: 'Rent', buy: 'Buy', both: 'Rent or buy' }[p.intent] || p.intent })
      }
      if (p.budget_min != null || p.budget_max != null) {
        rows.push({ label: 'Budget', value: `฿${money(p.budget_min)} – ฿${money(p.budget_max)}` })
      }
      if (Array.isArray(p.areas) && p.areas.length) rows.push({ label: 'Areas', value: p.areas.join(', ') })
      if (p.bedrooms) rows.push({ label: 'Bedrooms', value: p.bedrooms })
      if (p.property_type && p.property_type !== 'unknown') rows.push({ label: 'Type', value: p.property_type })
      if (p.move_in) rows.push({ label: 'Move-in', value: p.move_in })
      if (Array.isArray(p.must_haves) && p.must_haves.length) rows.push({ label: 'Must-have', value: p.must_haves.join(', ') })
      if (p.language) rows.push({ label: 'Language', value: p.language.toUpperCase() })
      if (p.notes) rows.push({ label: 'Notes', value: p.notes })
      return rows
    },
  },

  mounted() {
    this.loadDays()
    this.openConversationFromQuery()
  },

  methods: {
    // Opens the conversation named by ?user=<id> — e.g. arriving from the Leads
    // screen's "View chat" button for a web lead. We only have the id, so open a
    // bare row (the header name/date fill in once its messages load) and try to
    // upgrade it with the real row from the flat "Show all chat" list.
    async openConversationFromQuery() {
      const userId = this.$route.query.user
      if (!userId) return
      this.openConversation({ user_id: userId })
      try {
        const { data } = await chatbotAxios.get('/api/v1/conversations/web', { params: { limit: 100, offset: 0 } })
        const match = (data.data || data || []).find(c => c.user_id === userId)
        if (match && this.selectedUser && this.selectedUser.user_id === userId) {
          this.selectedUser = { ...this.selectedUser, ...match }
        }
      } catch (_) { /* header just keeps the short-id fallback */ }
    },

    switchViewMode(mode) {
      if (mode === this.viewMode) return
      this.viewMode = mode
      if (mode === 'day' && this.days.length === 0) this.loadDays()
      if (mode === 'all' && this.allConversations.length === 0) this.loadAllConversations()
    },

    refresh() {
      if (this.viewMode === 'day') this.loadDays()
      else this.loadAllConversations()
    },

    async loadDays() {
      this.loadingDays = true
      try {
        const { data } = await chatbotAxios.get('/api/v1/conversations/web/stats/daily', { params: { days: DAY_VIEW_WINDOW } })
        const series = data.data || data || []
        // Newest day first, keep each day's own expand/conversations state if already loaded
        const prior = new Map(this.days.map(d => [d.date, d]))
        this.days = [...series].reverse().map(point => {
          const existing = prior.get(point.date)
          const conversations = existing ? existing.conversations : []
          return {
            date: point.date,
            count: point.count,
            expanded: existing ? existing.expanded : false,
            loading: false,
            loadingMore: false,
            conversations,
            hasMore: conversations.length < point.count,
          }
        })
      } catch (e) {
        this.showToast(`Failed to load chat history: ${e.message}`, 'danger')
      } finally {
        this.loadingDays = false
      }
    },

    async toggleDay(day) {
      const idx = this.days.findIndex(d => d.date === day.date)
      if (idx === -1) return
      const expanded = !this.days[idx].expanded
      this.$set(this.days, idx, { ...this.days[idx], expanded })

      if (expanded && this.days[idx].conversations.length === 0 && this.days[idx].count > 0) {
        this.$set(this.days, idx, { ...this.days[idx], loading: true })
        try {
          const { data } = await chatbotAxios.get('/api/v1/conversations/web', { params: { date: day.date, limit: PAGE_SIZE, offset: 0 } })
          const conversations = data.data || data || []
          const hasMore = conversations.length < this.days[idx].count
          this.$set(this.days, idx, {
            ...this.days[idx], conversations, hasMore, loading: false,
          })
        } catch (e) {
          this.$set(this.days, idx, { ...this.days[idx], loading: false })
          this.showToast(`Failed to load conversations: ${e.message}`, 'danger')
        }
      }
    },

    async loadMoreDay(day) {
      const idx = this.days.findIndex(d => d.date === day.date)
      if (idx === -1) return
      this.$set(this.days, idx, { ...this.days[idx], loadingMore: true })
      try {
        const offset = this.days[idx].conversations.length
        const { data } = await chatbotAxios.get('/api/v1/conversations/web', { params: { date: day.date, limit: PAGE_SIZE, offset } })
        const more = data.data || data || []
        const conversations = [...this.days[idx].conversations, ...more]
        const hasMore = conversations.length < this.days[idx].count
        this.$set(this.days, idx, {
          ...this.days[idx], conversations, hasMore, loadingMore: false,
        })
      } catch (e) {
        this.$set(this.days, idx, { ...this.days[idx], loadingMore: false })
        this.showToast(`Failed to load more: ${e.message}`, 'danger')
      }
    },

    async loadAllConversations() {
      this.loadingAll = true
      try {
        const { data } = await chatbotAxios.get('/api/v1/conversations/web', { params: { limit: PAGE_SIZE, offset: 0 } })
        const conversations = data.data || data || []
        this.allConversations = conversations
        this.allHasMore = conversations.length === PAGE_SIZE
      } catch (e) {
        this.showToast(`Failed to load chats: ${e.message}`, 'danger')
      } finally {
        this.loadingAll = false
      }
    },

    async loadMoreAllConversations() {
      this.loadingMoreAll = true
      try {
        const { data } = await chatbotAxios.get('/api/v1/conversations/web', { params: { limit: PAGE_SIZE, offset: this.allConversations.length } })
        const more = data.data || data || []
        this.allConversations = [...this.allConversations, ...more]
        this.allHasMore = more.length === PAGE_SIZE
      } catch (e) {
        this.showToast(`Failed to load more: ${e.message}`, 'danger')
      } finally {
        this.loadingMoreAll = false
      }
    },

    async openConversation(conv) {
      this.selectedUser = conv
      this.showHistoryPanel = true
      this.chatMessages = []
      this.historyLoading = true
      this.loadProfile(conv.user_id)
      try {
        const { data } = await chatbotAxios.get(`/api/v1/conversations/${conv.user_id}/messages`)
        this.chatMessages = this.parseMessages(data.data || data || [])
      } catch (e) {
        this.showToast(`Failed to load history: ${e.message}`, 'danger')
      } finally {
        this.historyLoading = false
      }
    },

    async loadProfile(userId) {
      this.customerProfile = null
      try {
        const { data } = await chatbotAxios.get(`/api/v1/conversations/${userId}/profile`)
        this.customerProfile = data.data || null
      } catch (e) {
        console.warn('Failed to load customer profile:', e.message)
      }
    },

    async resetProfile() {
      if (!this.selectedUser) return
      try {
        await chatbotAxios.delete(`/api/v1/conversations/${this.selectedUser.user_id}/profile`)
        this.customerProfile = null
        this.showToast('Customer profile reset')
      } catch (e) {
        this.showToast(`Failed to reset profile: ${e.message}`, 'danger')
      }
    },

    closeHistory() {
      this.showHistoryPanel = false
      this.selectedUser = null
      this.chatMessages = []
      this.customerProfile = null
    },

    parseMessages(raw) {
      return raw
        .filter(msg => msg.content && msg.content.trim())
        .map(msg => {
          const agentName = msg.agent_name || null
          return {
            role: msg.role,
            ...this.parseContent(msg.content.trim()),
            time: msg.created_at ? this.formatTime(msg.created_at) : null,
            agentName,
            agentAvatar: msg.agent_avatar || null,
            agentInitial: agentName ? agentName[0].toUpperCase() : null,
          }
        })
    },

    parseContent(content) {
      const match = content.match(/^\[image:(\d+)\]$/)
      if (match) return { type: 'image', imageMessageId: match[1], text: '' }
      return { type: 'text', text: content }
    },

    dayLabel(date) {
      const today = moment().format('YYYY-MM-DD')
      const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD')
      if (date === today) return 'Today'
      if (date === yesterday) return 'Yesterday'
      return moment(date).format('ddd, MMM D')
    },

    contactDisplayName(item) {
      if (!item) return 'Unknown'
      return item.display_name || this.shortId(item.user_id)
    },

    fullDisplayName(item) {
      if (!item) return 'Unknown'
      return item.display_name || item.user_id
    },

    avatarInitial(name) { return name ? name[0].toUpperCase() : '?' },
    shortId(id) { return id.length > 16 ? `${id.slice(0, 14)}…` : id },
    formatTime(raw) {
      if (!raw) return '-'
      return moment(raw).format('HH:mm')
    },
    formatDateTime(raw) {
      if (!raw) return '-'
      return moment(raw).format('DD/MM/YY, HH:mm')
    },
    showToast(message, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: { title: message, icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon', variant },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.webchat-page {
  display: flex;
  height: calc(100vh - 180px);
  min-height: 500px;
  border-radius: 0.428rem;
  overflow: hidden;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);

  @media (max-width: 767px) {
    height: calc(100dvh - 130px);
    min-height: unset;
    flex-direction: column;
  }
}

.list-panel {
  width: 340px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #ebe9f1;
  background: #fff;
  overflow: hidden;

  @media (max-width: 767px) {
    width: 100% !important;
    border-right: none;
    flex: 1;
    height: 0;
  }
}

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 10px;
  flex-shrink: 0;
}

.period-toggle {
  padding: 0 16px 12px;
  flex-shrink: 0;
}

.day-list {
  flex: 1;
  overflow-y: auto;
}

.day-list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  color: #adb5bd;
  font-size: 0.92rem;
  text-align: center;
}

.day-group {
  border-bottom: 1px solid #f5f5f7;
}

.day-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 11px 16px;
  cursor: pointer;
  transition: background 0.12s;

  &:hover { background: #f8f7ff; }

  &--empty {
    color: #adb5bd;
  }
}

.day-header__chevron {
  flex-shrink: 0;
  color: #adb5bd;
}

.day-header__label {
  font-weight: 600;
  font-size: 0.94rem;
  flex: 1;
}

.day-header__count {
  font-size: 0.78rem;
  color: #adb5bd;
  background: #f0f0f5;
  border-radius: 20px;
  padding: 2px 9px;

  &--active {
    color: #7367f0;
    background: #eeebff;
    font-weight: 600;
  }
}

.day-conversations {
  background: #fafafa;
  padding: 2px 0 6px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px 8px 34px;
  cursor: pointer;
  transition: background 0.12s;

  &:hover { background: #f0eeff; }

  &--active {
    background: #eeebff;
    border-left: 3px solid #7367f0;
    padding-left: 31px;
  }

  &--flat {
    padding-left: 16px;

    &.contact-item--active {
      padding-left: 13px;
    }
  }
}

.contact-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.contact-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #3d3d3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-time {
  font-size: 0.75rem;
  color: #adb5bd;
}

.load-more-row {
  display: flex;
  justify-content: center;
  padding: 8px 16px 10px;
}

.load-more-btn {
  border: 1px solid #e0dff5;
  background: #fff;
  color: #7367f0;
  border-radius: 20px;
  padding: 5px 16px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) { background: #f0eeff; }
  &:disabled { opacity: 0.6; cursor: default; }
}

.spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

// ── History panel ────────────────────────────────────────────────────────────
.history-panel {
  flex: 1;
  min-width: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  @media (max-width: 767px) {
    position: fixed;
    left: 0; right: 0; bottom: 0;
    width: 100%;
    height: 92dvh;
    border-radius: 20px 20px 0 0;
    z-index: 1030;
    transform: translateY(100%);
    transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
    box-shadow: 0 -4px 32px rgba(0, 0, 0, 0.18);

    &.is-open { transform: translateY(0); }
  }
}

.history-no-selection {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  text-align: center;
  color: #6e6b7b;
}

.history-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1029;
}

.sheet-handle {
  width: 40px;
  height: 5px;
  background: #dee2e6;
  border-radius: 3px;
  margin: 10px auto 4px;
  flex-shrink: 0;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #ebe9f1;
  flex-shrink: 0;
}

// ── Customer profile strip ──────────────────────────────────────────────────
.cust-profile {
  flex-shrink: 0;
  border-bottom: 1px solid #ebe9f1;
  background: #f8f8fc;
}

.cust-profile__toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  font-size: 0.82rem;
  font-weight: 700;
  color: #6e6b7b;
  cursor: pointer;

  &:hover { color: #7367f0; }
}

.cust-profile__body {
  padding: 4px 16px 10px;
}

.cust-profile__row {
  display: flex;
  gap: 10px;
  padding: 3px 0;
  font-size: 0.84rem;
  line-height: 1.4;
}

.cust-profile__label {
  flex-shrink: 0;
  width: 88px;
  color: #a0a0b8;
  font-weight: 600;
}

.cust-profile__value {
  color: #3d3d3d;
  word-break: break-word;
}

.cust-profile__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed #e0dff0;
  font-size: 0.75rem;
}

.cust-profile__reset {
  background: transparent;
  border: 1px solid #e0dff5;
  color: #ea5455;
  border-radius: 20px;
  padding: 2px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: #fdeaea; }
}

.history-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px;
  background-color: #efeef4;
  background-image: radial-gradient(circle, rgba(115,103,240,0.08) 1px, transparent 1px);
  background-size: 28px 28px;
}

.msg-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.empty-history {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.msg-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 2px;
}
.msg-row--user { flex-direction: row; }
.msg-row--bot  { flex-direction: row-reverse; }

.msg-avatar { flex-shrink: 0; }

.msg-content {
  max-width: 75%;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.msg-content--user { align-items: flex-start; }
.msg-content--bot  { align-items: flex-end; }

.msg-bubble {
  padding: 10px 14px;
  border-radius: 18px;
  font-size: 1rem;
  line-height: 1.55;
  word-break: break-word;
}

.msg-text { white-space: pre-wrap; }

.msg-agent-name {
  font-size: 0.78rem;
  font-weight: 700;
  color: #7367f0;
  text-align: right;
  margin-bottom: 3px;
  padding-right: 2px;
}

.bubble-user {
  background: #ffffff;
  color: #2d2d3a;
  border-bottom-left-radius: 5px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.12);
}

.bubble-bot {
  background: #7367f0;
  color: #fff;
  border-bottom-right-radius: 5px;
  box-shadow: 0 1px 3px rgba(115,103,240,0.35);
}

.msg-time {
  font-size: 0.75rem;
  color: #a0a0b8;
  margin-top: 3px;
}
.msg-time--left  { text-align: left;  padding-left: 2px; }
.msg-time--right { text-align: right; padding-right: 2px; }
</style>
